import React, { useEffect } from "react";
import "./DataEstateCategoryCard.scss";
import { useDispatch, useSelector } from "react-redux";
import { FETCH_CATEGORY_CARD_DATA } from "../../DataTagConstants";
import { percentageCalculation } from "../../../../components/common/utilities";

export const CLASSIFICATION_COLORS = {
  MARKETING: "#FF8A65",
  FINANCIAL: "#f68942",
  PERSONAL: "#f68942",
  ORGANIZATIONAL: "#f68942",
  API: "#42A5F5",
  MEDICAL: "#EF5350",
  NO_SENSITIVITY: "#FFBB28",
  OTHER: "#8D8D8D",
};

const FIXED_CATEGORIES = [
  "PERSONAL",
  "MARKETING",
  "FINANCIAL",
  "ORGANIZATIONAL",
  "MEDICAL",
  "API",
  "NO_SENSITIVITY",
  "OTHER",
];

const categoryNames = {
  PERSONAL: "Personal",
  MARKETING: "Marketing",
  FINANCIAL: "Financial",
  ORGANIZATIONAL: "Organizational",
  MEDICAL: "Medical",
  API: "API",
  NO_SENSITIVITY: "No Sensitivity",
  OTHER: "Other",
};

function DataEstateCategoryCard() {
  const dispatch = useDispatch();
  const summaryDataRaw = useSelector(
    (state) => state.dataTagReducer.categoryCardData
  );

  const dataSourceId = useSelector(
    (state) => state.dataTagReducer.selectedDataSource?.id
  );

  console.log("Category Card Data:", summaryDataRaw);

  const [tooltip, setTooltip] = React.useState({
    show: false,
    x: 0,
    y: 0,
    text: "",
  });

  const [animate, setAnimate] = React.useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimate(true), 100);
    return () => clearTimeout(timer);
  }, [summaryDataRaw]);

  useEffect(() => {
    if (dataSourceId) {
      dispatch({
        type: FETCH_CATEGORY_CARD_DATA,
        payload: dataSourceId,
      });
    }
  }, [dataSourceId]);

  const summaryMap = {};
  let maxCount = 1;

  if (Array.isArray(summaryDataRaw)) {
    summaryDataRaw.forEach((item) => {
      const key = item["DataTagSensitivity.finalType"];
      const count = Number(item["DataTagSensitivity.count"]);
      summaryMap[key] = count;
    });

    const values = Object.values(summaryMap);
    if (values.length > 0) maxCount = Math.max(...values);
  }

  const totalCount = Object.values(summaryMap).reduce(
    (sum, val) => sum + val,
    0
  );

  const size = 230;
  const center = size / 2;
  const outerRadius = 90;
  const innerRadius = 28;

  const points = [];
  const totalPoints = FIXED_CATEGORIES.length * 2;

  for (let i = 0; i < totalPoints; i++) {
    const angle = (i / totalPoints) * 2 * Math.PI;
    const radius = i % 2 === 0 ? outerRadius : innerRadius;
    const x = center + radius * Math.cos(angle);
    const y = center + radius * Math.sin(angle);
    points.push({ x, y, angle });
  }

  return (
    <div className="star-wrapper">
      <svg width={size} height={size} className="star-svg">
        <circle
          cx={center}
          cy={center}
          r={innerRadius - 0.8}
          fill="#FFC107"
          // opacity="0.25"
        />

        {FIXED_CATEGORIES.map((cat, index) => {
          const count = summaryMap[cat] || 0;
          const opacity = maxCount ? count / maxCount : 0;

          const outerIdx = index * 2;

          const innerLeft = points[(outerIdx - 1 + totalPoints) % totalPoints];
          const outer = points[outerIdx];
          const innerRight = points[(outerIdx + 1) % totalPoints];

          const fillRadius =
            innerRadius + (outerRadius - innerRadius) * opacity;

          const scaledOuterX = center + fillRadius * Math.cos(outer.angle);
          const scaledOuterY = center + fillRadius * Math.sin(outer.angle);

          return (
            <polygon
              points={`
                      ${innerLeft.x},${innerLeft.y}
                      ${scaledOuterX},${scaledOuterY}
                      ${innerRight.x},${innerRight.y}
                    `}
              fill="#FFC107"
              onMouseEnter={(e) => {
                const rect = e.target.getBoundingClientRect();
                setTooltip({
                  show: true,
                  x: rect.x + rect.width / 2,
                  y: rect.y,
                  text: `${categoryNames[cat]}: ${count}`,
                });
              }}
              onMouseLeave={() => setTooltip({ show: false })}
              style={{ cursor: "pointer" }}
              className={`star-slice ${animate ? "animate" : ""}`}
            />
          );
        })}

        <polygon
          points={points.map((p) => `${p.x},${p.y}`).join(" ")}
          fill="gray"
          fillOpacity={0.1}
          className="star-shape"
        />

        {points.map((p, idx) => {
          if (idx % 2 !== 0) return null;

          const cat = FIXED_CATEGORIES[idx / 2];
          const count = summaryMap[cat] || 0;

          const percentageCalculate = percentageCalculation(
            [{ count }],
            totalCount
          )[0]?.unRoundPercentage;

          const numericPercentage = Number(percentageCalculate);

          const hasData =
            count > 0 &&
            Number.isFinite(numericPercentage) &&
            numericPercentage > 0;

          const percentage = hasData ? numericPercentage.toFixed(1) : "0.0";

          const angleDeg = (p.angle * 180) / Math.PI;

          let textAnchor = "middle";
          if (angleDeg > 90 && angleDeg < 270) textAnchor = "end"; // left side
          else if (angleDeg < 90 || angleDeg > 270) textAnchor = "start"; // right side

          const LABEL_OFFSET = 15;

          const labelX =
            center + (outerRadius + LABEL_OFFSET) * Math.cos(p.angle);
          const labelY =
            center + (outerRadius + LABEL_OFFSET) * Math.sin(p.angle);

          return (
            <text
              key={cat}
              x={labelX}
              y={labelY}
              className={`star-label ${!hasData ? "disabled" : ""}`}
              textAnchor={textAnchor}
              dominantBaseline="middle"
            >
              <tspan x={labelX} dy="0">
                {categoryNames[cat]}
              </tspan>
              <tspan x={labelX} dy="1.1em">
                ({percentage}%)
              </tspan>
            </text>
          );
        })}
      </svg>

      {tooltip?.show && <div className="star-tooltip show">{tooltip.text}</div>}
    </div>
  );
}

export default DataEstateCategoryCard;
